import { mount, unmount } from "svelte";
// This css import is to let Vite pick up the css, so that it will be generated and included in the dist folder
// The css is actually fetched and included as shadowRoot.adoptedStyleSheets at runtime.
import "@tomra/design-system/src/config/tailwind.config.css";
import "./app.css";
import App from "./App.svelte";
import { getBaseUrl } from "$lib/baseUrlResolver";
import { logger } from "$lib/logger";
import {
  bootstrapToken,
  tokenRefreshCallback,
} from "$lib/stores/bootstrapTokenStore";

class ImportInstallationsUI extends HTMLElement {
  private app: Record<string, undefined> | null = null;
  private _bootstrapToken: string | undefined;
  private _audience: string | undefined;

  /**
   * Bootstrap token provided by the host application (service-portal).
   * Set as a JS property, never as an HTML attribute (security requirement).
   */
  get bootstrapToken(): string | undefined {
    return this._bootstrapToken;
  }

  set bootstrapToken(value: string | undefined) {
    this._bootstrapToken = value;
    if (value) {
      // Update the store so authenticatedFetch uses the new token
      bootstrapToken.set(value);
      logger.info("Bootstrap token updated via property setter");
    }
  }

  /**
   * Target audience for the bootstrap token.
   */
  get audience(): string | undefined {
    return this._audience;
  }

  set audience(value: string | undefined) {
    this._audience = value;
  }

  async connectedCallback() {
    if (!this.shadowRoot) {
      try {
        this.attachShadow({ mode: "open" });
      } catch (e) {
        logger.error("FAILED to attach shadowRoot", e);
        return;
      }
    }

    // Collect HTML attributes
    const attributes: Record<string, string> = {};
    for (const attr of this.attributes) {
      attributes[attr.name] = attr.value;
    }

    // Add JS properties (bootstrapToken, audience) to props
    // These are set by the host app and take precedence
    const props: Record<string, string | undefined> = {
      ...attributes,
      bootstrapToken: this._bootstrapToken,
      audience: this._audience,
    };

    const customElementId =
      attributes["id"] ??
      `import-installations-ui-${Math.random().toString(36).substring(2, 15)}`;

    const container = document.createElement("div");
    container.id = customElementId;
    this.shadowRoot!.appendChild(container);

    await this.injectStyles();

    // Set up the token refresh callback
    // When the MFE needs a fresh token, it will call this callback
    // which dispatches an event to the host app
    tokenRefreshCallback.set(() => {
      this.dispatchEvent(
        new CustomEvent("mfe-token-refresh-needed", {
          bubbles: true,
          composed: true,
          detail: { audience: this._audience || "import-installations-api" },
        }),
      );
    });

    try {
      this.app = mount(App, {
        target: container,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        props: props as any,
      });
    } catch (e) {
      logger.error("ERROR mounting Svelte", e);
    }
  }

  /**
   * Inject CSS by fetching from server and adopt into the shadow root
   */
  private async injectStyles(): Promise<undefined> {
    try {
      if (import.meta.env.DEV) {
        await this.injectViteDevStyles();
        return;
      }

      const baseUrl = getBaseUrl();
      const cssUrl = `${baseUrl}/import-installations-ui.css`;

      const response = await fetch(cssUrl, { method: "GET", mode: "cors" });

      if (!response.ok) {
        logger.error(
          `NON-OK status loading CSS: ${response.status} ${response.statusText}`,
        );
        return;
      }

      const cssText = await response.text();

      this.adoptCss(cssText);
    } catch (e) {
      logger.error("Unexpected fatal error loading CSS", e);
    }
  }

  private adoptCss(cssText: string): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(cssText);

    if (this.shadowRoot) {
      this.shadowRoot.adoptedStyleSheets = [
        ...this.shadowRoot.adoptedStyleSheets,
        sheet,
      ];
    } else {
      logger.error(`CSS: shadowRoot is NULL after fetch successful load.`);
    }
  }

  private async injectViteDevStyles(): Promise<void> {
    const cssText = Array.from(
      document.querySelectorAll<HTMLStyleElement>("style[data-vite-dev-id]"),
    )
      .filter((styleElement) => {
        const viteDevId = styleElement.dataset.viteDevId ?? "";
        return (
          viteDevId.includes(
            "@tomra/design-system/src/config/tailwind.config.css",
          ) || viteDevId.endsWith("/src/app.css")
        );
      })
      .map((styleElement) => styleElement.textContent ?? "")
      .join("\n");

    this.adoptCss(cssText);
  }

  disconnectedCallback() {
    logger.info("disconnectedCallback fired — unmounting");

    // Clear the token refresh callback
    tokenRefreshCallback.set(null);

    if (this.app) {
      unmount(this.app)
        .catch((error) => {
          logger.error("Error unmounting Svelte", error);
        })
        .finally(() => {
          this.app = null;
        });
    }
  }
}

customElements.define("import-installations-ui", ImportInstallationsUI);
